import { useEffect, useImperativeHandle } from 'react';
import { Stack, Text } from '@adc/parallax-component-library';
import { useForm, useStore } from '@tanstack/react-form';

import { CLMPhoneInput } from '~/components/inputs/CLMPhoneNumberInput';
import type { PatientIntroFormProps } from '~/features/patientCreation/types';
import { patientIntroFormSchema } from '~/features/patientCreation/validations';
import { buildTestId } from '~/utils/buildTestId';
import { getErrorProps } from '~/utils/forms/getErrorProps';

export const PatientIntroForm = ({
  ref,
  onSubmit,
  setIsNextDisabled,
  testID,
  defaultValues,
}: PatientIntroFormProps) => {
  const form = useForm({
    defaultValues: {
      phone: '',
      ...defaultValues,
    },
    validators: {
      onMount: patientIntroFormSchema,
      onChange: patientIntroFormSchema,
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value);
    },
  });

  const isNextDisabled = useStore(
    form.store,
    (state) => !state.canSubmit || state.isSubmitting,
  );

  useEffect(() => {
    setIsNextDisabled(isNextDisabled);
  }, [isNextDisabled, setIsNextDisabled]);

  useImperativeHandle(
    ref,
    () => ({
      submitForm: form.handleSubmit,
    }),
    [form.handleSubmit],
  );

  return (
    <Stack gap="$6" paddingTop="$2" testID={testID}>
      <Text
        type="$bodyBase.medium"
        color="$text.100"
        testID={buildTestId({ testID, suffix: 'title' })}
      >
        Patient Information
      </Text>

      <Stack flexDirection="row" gap="$6">
        <Stack flex={0.5} flexShrink={1} flexBasis="0%">
          <form.Field name="phone">
            {(field) => (
              <CLMPhoneInput
                testID={buildTestId({ testID, suffix: ['input', 'phone'] })}
                label="Phone Number"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                {...getErrorProps(field)}
              />
            )}
          </form.Field>
        </Stack>
      </Stack>
    </Stack>
  );
};
